import {
  CONSUMABLE_TEMPLATES,
  type ConsumableBatch,
} from './procedural';
import { normalizeAnyConsumableBatch as normalizeConsumableBatch } from './configured';

export interface ConsumableInventoryStack {
  id: string;
  stackKey?: string;
  qty: number;
  batch?: ConsumableBatch;
}

const RARITY_LABELS: Record<string, string> = {
  common: 'commun', rare: 'rare', epic: 'épique', legendary: 'légendaire',
};

export function consumableInventoryKey(stack: Partial<ConsumableInventoryStack>): string {
  return stack.stackKey || stack.batch?.stackKey || String(stack.id || '');
}

export function isProceduralConsumableStack(stack: any): stack is ConsumableInventoryStack & { batch: ConsumableBatch } {
  return !!stack?.batch?.stackKey && !!stack?.batch?.templateId && Array.isArray(stack?.batch?.effects);
}

/**
 * Migre les anciennes signatures vers l'identité v2, refuse les batches
 * mécaniquement invalides et fusionne uniquement les lots canoniquement égaux.
 */
export function migrateConsumableInventoryState(state: any): { migrated: number; dropped: number } {
  const items = Array.isArray(state?.inventory?.items) ? state.inventory.items : null;
  if (!items) return { migrated: 0, dropped: 0 };
  const merged = new Map<string, any>();
  let migrated = 0;
  let dropped = 0;
  for (const stack of items) {
    if (!stack || stack.id == null) { dropped++; continue; }
    const qty = Math.max(0, Math.floor(Number(stack.qty) || 0));
    if (qty <= 0) { dropped++; continue; }
    if (stack.batch) {
      const batch = normalizeConsumableBatch(stack.batch);
      if (!batch) { dropped++; continue; }
      if (stack.stackKey !== batch.stackKey || stack.batch.stackKey !== batch.stackKey) migrated++;
      const existing = merged.get(batch.stackKey);
      if (existing) existing.qty += qty;
      else merged.set(batch.stackKey, {
        ...stack, id: batch.templateId, itemId: stack.itemId || batch.templateId,
        stackKey: batch.stackKey, batch, qty,
      });
      continue;
    }
    // Les modèles procéduraux n'existent jamais sans batch signé.
    if (CONSUMABLE_TEMPLATES[String(stack.id)]) { dropped++; continue; }
    const key = String(stack.stackKey || stack.id);
    const existing = merged.get(key);
    if (existing) existing.qty += qty;
    else merged.set(key, { ...stack, qty });
  }
  state.inventory.items = [...merged.values()];
  return { migrated, dropped };
}

export function consumableItemView(stack: any, catalog: Record<string, any>): any | null {
  const templateId = stack?.batch?.templateId || stack?.id;
  const template = templateId ? catalog[templateId] : null;
  if (!template || template.cat !== 'consommable') return null;
  if (!isProceduralConsumableStack(stack)) return template.combat ? { ...template, qty: stack.qty || 1 } : null;
  const batch = stack.batch;
  return {
    ...template,
    id: template.id,
    inventoryKey: consumableInventoryKey(stack),
    stackKey: batch.stackKey,
    batch,
    qty: stack.qty || 1,
    rarity: batch.rarity,
    levelReq: batch.itemLevel,
    name: `${template.name} · ${RARITY_LABELS[batch.rarity] || batch.rarity} ${batch.qualityStars}★`,
    combat: { effect: 'procedural' },
  };
}
